import * as React from "react";
import Typography from "@mui/material/Typography";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { Link, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { theme } from "../../App";
import BaseButton from "../BaseButton";
import MobileDrawerMenu from "./MobileDrawerMenu";
import { zindex, appRed, appDarkBlue, navbarBlue } from "../../Classes/theme";
import AppIcon from "./AppIcon";
import { Motion } from "../Motion/Motion";

export function withAlpha(color, alpha) {
  color = color.trim();

  // 1) Already rgba?
  if (color.startsWith('rgba')) {
    // replace existing alpha
    return color.replace(/rgba\(\s*([^,]+),([^,]+),([^,]+),([^)]+)\)/,
      (_, r, g, b) => `rgba(${r.trim()},${g.trim()},${b.trim()},${alpha})`);
  }
  // 2) rgb(...) → rgba(...)
  if (color.startsWith('rgb(')) {
    return color.replace(
      /rgb\(\s*([^,]+),([^,]+),([^)]+)\)/,
      (_, r, g, b) => `rgba(${r.trim()},${g.trim()},${b.trim()},${alpha})`
    );
  }
  // 3) hex → rgba
  let hex = color.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(ch => ch + ch).join('');
  }
  if (hex.length !== 6) {
    console.warn('Invalid hex color:', color);
    return color;
  }
  const intVal = parseInt(hex, 16);
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export const NAVBAR_HEIGHT = 50; // px
export const NAVBAR_SPACE = { xs: "14px", sm: "50px", md: "50px" };

export const publicNavbar = [
  { name: "Domů", path: "/", id: null },
  { name: "Aplikace", path: "/", id: 'all_apps' },
  { name: "Kontakt", path: "/kontakt", id: null },
];


export default function Navbar({
  color = navbarBlue,
  tryButtonColor = appRed,
  tryButtonColorHover = appRed,
  dontHide = false,
  whiteVersion = false,
  threshold = 0,
  defaultColor = "transparent",
  logoWhite = false,
}) {
  const xxs = useMediaQuery("(max-width:540px)");
  const navigate = useNavigate();
  const sandwichMenu = useMediaQuery(theme.breakpoints.down("md"));
  const mobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [hovered, setHovered] = React.useState(null);
  // Mobile menu
  const [openSandwich, setOpenSandwich] = React.useState(false);
  const [bgTransparent, setBgTransparent] = React.useState(false);

  React.useEffect(() => {
    if (!openSandwich) {
      setTimeout(() => {
        setBgTransparent(false);
      }, 250);
    } else {
      setBgTransparent(true);
    }
  }, [openSandwich]);

  const elevated = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: window ? window : undefined,
  });

  const useElevate = dontHide ? true : elevated;


  return (
    <Box
      sx={{
        height: `fit-content`,
        width: "100%",
        bgcolor: useElevate ? withAlpha(color, 0.85) : defaultColor,
        position: "fixed",
        top: 0,
        zIndex: zindex.navbar,
        boxShadow:
          useElevate || openSandwich
            ? "0px 2px 5px 0px rgba(0,0,0,0.2)"
            : "none",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: "0px !important",
          zIndex: zindex.navbar,
          height: `fit-content`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: `${NAVBAR_HEIGHT}px`,
            py: "8px",
            px: NAVBAR_SPACE,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            alignContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              alignContent: "center",
              mr: "auto",
            }}
          >
            <Box
              component={Link}
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: 'center',
                alignContent: 'center',
                textDecoration: "none",
                p: "10px",
                pl: "0px",
                "&:hover": {
                  cursor: "pointer",
                },
              }}
              to="/"
            >
              <Motion scale={1.02}>
                <Box sx={{ height: '30px' }}>
                  <AppIcon textColor={elevated || logoWhite ? 'white' : "#1A1C1E"} />
                </Box>
              </Motion>
            </Box>

          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              alignContent: "center",
              ml: "auto",
            }}
          >
            {!sandwichMenu &&
              publicNavbar.map((nav, index) => (
                <Box
                  key={`NavText${index}`}
                  name={`NavText${index}`}
                  component={Link}
                  sx={{
                    textDecoration: "none",
                    mx: "8px",
                    px: "10px",
                    py: "8px",
                    color: "white",
                  }}
                  onMouseEnter={(e) => {
                    setHovered(e.target.name);
                  }}
                  onMouseLeave={(e) => {
                    if (e.target.name == hovered) {
                      setHovered(null);
                    }
                  }}
                  onClick={(e) => {
                    const id = nav.id;
                    if (id) {
                      e.preventDefault();
                      const el = document.getElementById(id);
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth" });
                      } else {
                        navigate(nav.path);
                      }
                    }

                    if (e.target.name == hovered) {
                      setHovered(null);
                    }

                  }}
                  onContextMenu={(e) => {
                    setHovered(null);
                  }}
                  to={nav.path}
                >
                  <Typography
                    variant="button"
                    sx={{
                      whiteSpace: 'nowrap',
                      lineHeight: 'normal',
                      color: "white",
                      transform:
                        hovered == `NavText${index}`
                          ? "translateY(-3px)"
                          : "translateY(0px)",
                      transitionDuration: "200ms",
                    }}

                  >
                    {nav.name}
                  </Typography>
                </Box>
              ))}
          </Box>

          {sandwichMenu && (
            <Box sx={{ ml: "10px" }}>
              <MobileDrawerMenu
                whiteVersion={!useElevate && xxs}
                state={openSandwich}
                setState={setOpenSandwich}
              />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
